import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Download, Image, Upload, Palette,
  LayoutGrid, Loader2, X, GripVertical, Layers, ChevronDown,
  Sparkles, Send, Clock, Calendar, MessageSquare, ChevronRight,
  Instagram, Facebook, CheckCircle, AlertCircle,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { SlideCanvas } from './SlideCanvas';
import { SlideStrip } from './SlideStrip';
import { LayerPanel } from '../reels/LayerPanel';
import { PropertyInspector } from '../reels/PropertyInspector';
import CanvaTextToolbar from '../reels/FloatingTextToolbar';
import apiClient from '../../../utils/api';
import { usePlan } from '../../../contexts/PlanContext';
import { useIsMobile } from '../../../hooks/useIsMobile';
import MobileCoachMark from '../../MobileCoachMark';

const ASPECT_CONFIGS = {
  '1:1': { w: 1080, h: 1080, previewW: 400, previewH: 400 },
  '4:5': { w: 1080, h: 1350, previewW: 360, previewH: 450 },
  '9:16': { w: 1080, h: 1920, previewW: 250, previewH: 444 },
};

function createBlankSlide(bgColor = '#1a1a2e') {
  return {
    id: `slide_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
    background_color: bgColor,
    background_image: null,
    bg_transform: { x: 0, y: 0, scale: 1, rotation: 0 },
    layers: [],
  };
}

// Listing-status → badge label + CTA copy so the deck matches what the user picked.
const STATUS_BADGE = {
  new_listing: 'JUST LISTED',
  coming_soon: 'COMING SOON',
  open_house: 'OPEN HOUSE',
  price_reduced: 'PRICE REDUCED',
  under_contract: 'UNDER CONTRACT',
  sold: 'JUST SOLD',
};
const STATUS_CTA_TITLE = {
  open_house: 'Join Us at the\nOpen House',
  sold: 'Another Home\nSold!',
  price_reduced: 'New Price —\nAct Fast',
  coming_soon: 'Coming Soon —\nStay Tuned',
  under_contract: 'Under Contract\nin Record Time',
  new_listing: 'Schedule a\nPrivate Showing',
};
const STATUS_POST_TYPE = {
  new_listing: 'new_listing',
  coming_soon: 'coming_soon',
  open_house: 'open_house',
  price_reduced: 'price_reduced',
  under_contract: 'under_contract',
  sold: 'sold',
};

export const CarouselEditor = () => {
  const isMobile = useIsMobile(900);
  const [mobileSheet, setMobileSheet] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const compIdParam = searchParams.get('id');
  const propertyParam = searchParams.get('property');
  const carouselTemplate = location.state?.carouselTemplate || null;

  const [compId, setCompId] = useState(compIdParam && compIdParam !== 'new' ? compIdParam : null);
  const { handleApiError } = usePlan() || {};
  const [name, setName] = useState('Untitled Carousel');
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [slides, setSlides] = useState([createBlankSlide()]);
  const [activeSlideIdx, setActiveSlideIdx] = useState(0);
  const [selectedLayerId, setSelectedLayerId] = useState(null);
  const [isBgSelected, setIsBgSelected] = useState(false);
  const [propertyData, setPropertyData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [dragOverCanvas, setDragOverCanvas] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const fileInputRef = useRef(null);
  const activeIdxRef = useRef(0);

  // Caption & Schedule state
  const [showSchedulePanel, setShowSchedulePanel] = useState(false);
  const [caption, setCaption] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [generatingCaption, setGeneratingCaption] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [publishProgress, setPublishProgress] = useState('');
  const pollRef = useRef(null);

  // Auto-generate caption when schedule panel opens for the first time with property data
  const captionAutoGenRef = useRef(false);
  useEffect(() => {
    if (showSchedulePanel && propertyData && !caption && !captionAutoGenRef.current) {
      captionAutoGenRef.current = true;
      generateCaption();
    }
  }, [showSchedulePanel, propertyData]);

  // Load connected social accounts when the publish panel opens.
  useEffect(() => {
    if (showSchedulePanel && accounts.length === 0 && !loadingAccounts) {
      setLoadingAccounts(true);
      apiClient.get('/social/accounts')
        .then(res => {
          const accts = res.data.accounts || [];
          setAccounts(accts);
          setSelectedAccountIds(accts.map(a => a.account_id));
        })
        .catch(() => toast.error('Failed to load social accounts'))
        .finally(() => setLoadingAccounts(false));
    }
  }, [showSchedulePanel]);

  // Stop polling if the editor unmounts.
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const toggleAccount = (accountId) => {
    setSelectedAccountIds(prev =>
      prev.includes(accountId) ? prev.filter(id => id !== accountId) : [...prev, accountId]
    );
  };

  // Keep ref in sync
  activeIdxRef.current = activeSlideIdx;

  const config = ASPECT_CONFIGS[aspectRatio] || ASPECT_CONFIGS['1:1'];
  const activeSlide = slides[activeSlideIdx] || slides[0];

  // ── Load existing composition or property data ──
  useEffect(() => {
    if (loaded) return;
    const load = async () => {
      if (compIdParam && compIdParam !== 'new') {
        try {
          const res = await apiClient.get(`/carousel/compositions/${compIdParam}`);
          setName(res.data.name || 'Untitled Carousel');
          setAspectRatio(res.data.aspect_ratio || '1:1');
          setSlides(res.data.slides?.length > 0 ? res.data.slides : [createBlankSlide()]);
          setPropertyData(res.data.property_data || null);
          setCompId(compIdParam);
        } catch {
          toast.error('Failed to load carousel');
        }
      } else if (carouselTemplate) {
        // Initialize from a carousel template
        const tpl = carouselTemplate;
        setName(tpl.name || 'Untitled Carousel');
        setAspectRatio(tpl.aspect_ratio || '1:1');
        if (tpl.slides?.length > 0) {
          setSlides(tpl.slides.map(s => ({ ...s })));
        }
      } else if (propertyParam) {
        try {
          const pd = JSON.parse(decodeURIComponent(propertyParam));
          setPropertyData(pd);
          setName(pd.address ? `${pd.address} Carousel` : 'Property Carousel');
          const badge = STATUS_BADGE[pd.listingStatus] || (pd.property_type || 'JUST LISTED');
          const ctaTitle = STATUS_CTA_TITLE[pd.listingStatus] || 'Schedule a\nPrivate Showing';
          // Auto-generate 5 slides from property data
          const photos = pd.photos || [];
          const newSlides = [];
          // Slide 1: Hero with address
          const heroLayers = [
            { id: 'l_hero_tag', type: 'rect', x: 60, y: 60, width: 250, height: 60, fill: '#8B5CF6', borderRadius: 8, opacity: 0.9 },
            { id: 'l_hero_label', type: 'text', x: 70, y: 65, width: 230, height: 55, content: badge, fontSize: 32, fontColor: 'white', fontWeight: 'bold', textAlign: 'center' },
            { id: 'l_hero_addr', type: 'text', x: 60, y: 850, width: 960, height: 120, content: pd.address || 'Property Address', fontSize: 52, fontColor: 'white', fontWeight: 'bold', textAlign: 'left', shadow: { color: '#000', blur: 6, offsetX: 2, offsetY: 2 } },
            { id: 'l_hero_price', type: 'text', x: 60, y: 950, width: 500, height: 80, content: pd.price || '$---,---', fontSize: 44, fontColor: '#C9A96E', fontWeight: 'bold', textAlign: 'left', shadow: { color: '#000', blur: 4, offsetX: 1, offsetY: 1 } },
          ];
          // Open House: surface the date/time the user entered.
          if (pd.listingStatus === 'open_house' && pd.statusExtra) {
            heroLayers.push({ id: 'l_hero_event', type: 'text', x: 60, y: 760, width: 960, height: 70, content: `Open House: ${pd.statusExtra}`, fontSize: 38, fontColor: 'white', fontWeight: 'bold', textAlign: 'left', bgHighlight: { enabled: true, color: '#8B5CF6', opacity: 0.85, padding: 12, borderRadius: 8 } });
          }
          newSlides.push({
            ...createBlankSlide(),
            background_image: photos[0] || null,
            layers: heroLayers,
          });
          // Slide 2: Features
          newSlides.push({
            ...createBlankSlide('#0f172a'),
            background_image: photos[1] || null,
            layers: [
              { id: 'l_feat_title', type: 'text', x: 60, y: 100, width: 960, height: 80, content: 'Property Features', fontSize: 48, fontColor: 'white', fontWeight: 'bold', textAlign: 'left' },
              { id: 'l_feat_beds', type: 'text', x: 60, y: 400, width: 450, height: 70, content: `${pd.beds || '—'} Bedrooms`, fontSize: 40, fontColor: 'white', fontWeight: 'bold', bgHighlight: { enabled: true, color: '#000', opacity: 0.5, padding: 12, borderRadius: 8 } },
              { id: 'l_feat_baths', type: 'text', x: 60, y: 500, width: 450, height: 70, content: `${pd.baths || '—'} Bathrooms`, fontSize: 40, fontColor: 'white', fontWeight: 'bold', bgHighlight: { enabled: true, color: '#000', opacity: 0.5, padding: 12, borderRadius: 8 } },
              { id: 'l_feat_sqft', type: 'text', x: 60, y: 600, width: 450, height: 70, content: `${pd.sqft || '—'} Sq Ft`, fontSize: 40, fontColor: 'white', fontWeight: 'bold', bgHighlight: { enabled: true, color: '#000', opacity: 0.5, padding: 12, borderRadius: 8 } },
            ],
          });
          // Slides 3-4: Photo showcases
          for (let i = 2; i < Math.min(4, photos.length); i++) {
            newSlides.push({
              ...createBlankSlide(),
              background_image: photos[i],
              layers: [],
            });
          }
          // Slide 5: CTA
          newSlides.push({
            ...createBlankSlide('#1e1b4b'),
            background_image: photos[4] || null,
            layers: [
              { id: 'l_cta_title', type: 'text', x: 140, y: 350, width: 800, height: 100, content: ctaTitle, fontSize: 52, fontColor: 'white', fontWeight: 'bold', textAlign: 'center', shadow: { color: '#000', blur: 8, offsetX: 2, offsetY: 2 } },
              { id: 'l_cta_rect', type: 'rect', x: 290, y: 550, width: 500, height: 70, fill: '#8B5CF6', borderRadius: 35, opacity: 0.95 },
              { id: 'l_cta_btn', type: 'text', x: 300, y: 555, width: 480, height: 60, content: 'Contact Agent', fontSize: 36, fontColor: 'white', fontWeight: 'bold', textAlign: 'center' },
              { id: 'l_cta_phone', type: 'text', x: 200, y: 700, width: 680, height: 60, content: pd.agent_phone || pd.agent_name || 'Call Today', fontSize: 32, fontColor: '#C9A96E', fontWeight: 'bold', textAlign: 'center' },
            ],
          });
          setSlides(newSlides);
        } catch (e) {
          console.error('Failed to parse property data', e);
        }
      }
      setLoaded(true);
    };
    load();
  }, [compIdParam, propertyParam, loaded]);

  // ── Slide management ──
  const addSlide = () => {
    if (slides.length >= 10) { toast.error('Maximum 10 slides'); return; }
    const newSlides = [...slides, createBlankSlide(activeSlide?.background_color)];
    setSlides(newSlides);
    setActiveSlideIdx(newSlides.length - 1);
    setSelectedLayerId(null);
  };

  const duplicateSlide = (idx) => {
    if (slides.length >= 10) { toast.error('Maximum 10 slides'); return; }
    const src = slides[idx];
    const dup = {
      ...JSON.parse(JSON.stringify(src)),
      id: `slide_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
      layers: src.layers.map(l => ({ ...l, id: `${l.id}_c${Date.now().toString(36)}` })),
    };
    const newSlides = [...slides];
    newSlides.splice(idx + 1, 0, dup);
    setSlides(newSlides);
    setActiveSlideIdx(idx + 1);
  };

  const removeSlide = (idx) => {
    if (slides.length <= 1) return;
    const newSlides = slides.filter((_, i) => i !== idx);
    setSlides(newSlides);
    setActiveSlideIdx(Math.min(activeSlideIdx, newSlides.length - 1));
    setSelectedLayerId(null);
  };

  const reorderSlides = (fromIdx, toIdx) => {
    const newSlides = [...slides];
    const [moved] = newSlides.splice(fromIdx, 1);
    newSlides.splice(toIdx, 0, moved);
    setSlides(newSlides);
    setActiveSlideIdx(toIdx);
  };

  // ── Layer management (per active slide) ──
  const updateSlide = useCallback((idx, updates) => {
    setSlides(prev => prev.map((s, i) => i === idx ? { ...s, ...updates } : s));
  }, []);

  const updateLayers = useCallback((newLayers) => {
    updateSlide(activeSlideIdx, { layers: newLayers });
  }, [activeSlideIdx, updateSlide]);

  const updateLayerProps = useCallback((layerId, props) => {
    setSlides(prev => prev.map((s, i) => {
      if (i !== activeSlideIdx) return s;
      return { ...s, layers: s.layers.map(l => l.id === layerId ? { ...l, ...props } : l) };
    }));
  }, [activeSlideIdx]);

  const selectedLayer = activeSlide?.layers?.find(l => l.id === selectedLayerId) || null;

  // ── Undo / Redo history for `slides` ──
  const pastRef = useRef([]);
  const futureRef = useRef([]);
  const suppressHistoryRef = useRef(false);
  const lastSnapshotRef = useRef(null);
  useEffect(() => {
    if (suppressHistoryRef.current) {
      suppressHistoryRef.current = false;
      lastSnapshotRef.current = JSON.stringify(slides);
      return;
    }
    const t = setTimeout(() => {
      const snap = JSON.stringify(slides);
      if (lastSnapshotRef.current !== null && lastSnapshotRef.current !== snap) {
        pastRef.current = [...pastRef.current.slice(-49), JSON.parse(lastSnapshotRef.current)];
        futureRef.current = [];
      }
      lastSnapshotRef.current = snap;
    }, 400);
    return () => clearTimeout(t);
  }, [slides]);

  const undoSlides = useCallback(() => {
    if (pastRef.current.length === 0) return;
    futureRef.current = [JSON.parse(JSON.stringify(slides)), ...futureRef.current.slice(0, 49)];
    const restored = pastRef.current.pop();
    suppressHistoryRef.current = true;
    setSlides(restored);
  }, [slides]);

  const redoSlides = useCallback(() => {
    if (futureRef.current.length === 0) return;
    pastRef.current = [...pastRef.current, JSON.parse(JSON.stringify(slides))];
    const restored = futureRef.current.shift();
    suppressHistoryRef.current = true;
    setSlides(restored);
  }, [slides]);

  const deleteSelectedLayer = useCallback(() => {
    if (!selectedLayerId) return;
    setSlides(prev => prev.map((s, i) => {
      if (i !== activeSlideIdx) return s;
      return { ...s, layers: (s.layers || []).filter(l => l.id !== selectedLayerId) };
    }));
    setSelectedLayerId(null);
  }, [selectedLayerId, activeSlideIdx]);

  // Keyboard shortcuts: Ctrl/Cmd+Z (undo), Ctrl/Cmd+Shift+Z or Ctrl/Cmd+Y
  // (redo), Delete/Backspace (remove selected layer). Skip while typing.
  useEffect(() => {
    const handler = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target.isContentEditable) return;
      const isMod = e.ctrlKey || e.metaKey;
      if (isMod && !e.shiftKey && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        undoSlides();
        return;
      }
      if ((isMod && e.shiftKey && e.key.toLowerCase() === 'z') || (isMod && e.key.toLowerCase() === 'y')) {
        e.preventDefault();
        redoSlides();
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedLayerId) {
        e.preventDefault();
        deleteSelectedLayer();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undoSlides, redoSlides, selectedLayerId, deleteSelectedLayer]);

  // ── Background ──
  const handleBgColorChange = (color) => {
    updateSlide(activeSlideIdx, { background_color: color, background_image: null });
  };

  const handleBgImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const currentIdx = activeIdxRef.current;

    for (const file of files) {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        // Add to uploaded images gallery
        setUploadedImages(prev => [...prev, { id: `img_${Date.now()}_${Math.random().toString(36).slice(2,6)}`, url: dataUrl, name: file.name }]);
        // Set as background of current slide
        updateSlide(currentIdx, { background_image: dataUrl });
      };
      reader.readAsDataURL(file);
    }
    // Reset input so same file can be re-uploaded
    e.target.value = '';
  };

  const applyImageToSlide = useCallback((imageUrl, slideIdx) => {
    const idx = slideIdx !== undefined ? slideIdx : activeIdxRef.current;
    updateSlide(idx, { background_image: imageUrl });
    toast.success(`Image applied to slide ${idx + 1}`);
  }, [updateSlide]);

  const handleImageClick = useCallback((imageUrl) => {
    // Apply immediately to active slide
    applyImageToSlide(imageUrl);
    // Also store as selected for "click slide to place" workflow
    setSelectedImageUrl(imageUrl);
  }, [applyImageToSlide]);

  const handleSlideSelect = useCallback((idx) => {
    setActiveSlideIdx(idx);
    setSelectedLayerId(null);
    setIsBgSelected(false);
    // If user has a selected image, apply it to the newly selected slide
    if (selectedImageUrl) {
      updateSlide(idx, { background_image: selectedImageUrl });
      toast.success(`Image applied to slide ${idx + 1}`);
      setSelectedImageUrl(null);
    }
  }, [selectedImageUrl, updateSlide]);

  const removeUploadedImage = (imgId) => {
    setUploadedImages(prev => prev.filter(img => img.id !== imgId));
  };

  // ── Drag from sidebar to canvas ──
  const handleSidebarDragStart = (e, imageUrl) => {
    e.dataTransfer.setData('text/image-url', imageUrl);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleCanvasDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDragOverCanvas(true);
  };

  const handleCanvasDragLeave = () => {
    setDragOverCanvas(false);
  };

  const handleCanvasDrop = (e) => {
    e.preventDefault();
    setDragOverCanvas(false);
    const imageUrl = e.dataTransfer.getData('text/image-url');
    if (imageUrl) {
      applyImageToSlide(imageUrl);
    }
  };

  const updateBgTransform = useCallback((transform) => {
    updateSlide(activeSlideIdx, { bg_transform: transform });
  }, [activeSlideIdx, updateSlide]);

  // ── Save ──
  const handleSave = async () => {
    setSaving(true);
    let savedId = compId;
    try {
      const payload = { name, aspect_ratio: aspectRatio, slides, property_data: propertyData };
      if (compId) {
        await apiClient.put(`/carousel/compositions/${compId}`, payload);
        toast.success('Carousel saved');
      } else {
        const res = await apiClient.post('/carousel/compositions', payload);
        savedId = res.data.composition_id;
        setCompId(savedId);
        toast.success('Carousel created');
        window.history.replaceState(null, '', `/carousel-editor?id=${savedId}`);
      }
    } catch {
      toast.error('Failed to save');
    }
    setSaving(false);
    return savedId;
  };

  // ── Render & Download ──
  const handleRender = async () => {
    setRendering(true);
    try {
      // Always save first to ensure DB has latest slides
      const renderCompId = await handleSave();
      if (!renderCompId) { toast.error('Save failed, cannot render'); setRendering(false); return; }

      const res = await apiClient.post(`/carousel/render/${renderCompId}/zip`);
      // Download ZIP
      const blob = new Blob([Uint8Array.from(atob(res.data.zip_base64), c => c.charCodeAt(0))], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.data.filename || 'carousel.zip';
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${slides.length} slides`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Render failed');
    }
    setRendering(false);
  };

  // ── Aspect ratio change ──
  const handleAspectChange = (ratio) => {
    setAspectRatio(ratio);
  };

  // ── Caption generation ──
  const generateCaption = async () => {
    setGeneratingCaption(true);
    try {
      const details = propertyData || {};
      const postType = STATUS_POST_TYPE[details.listingStatus] || 'new_listing';
      const res = await apiClient.post('/ai/generate-caption', {
        property_details: {
          beds: details.beds || '',
          baths: details.baths || '',
          sqft: details.sqft || '',
          address: details.address || '',
          price: details.price || '',
          description: details.property_summary || details.description || '',
          status_detail: details.statusExtra || '',
        },
        post_type: postType,
        caption_style: 'minimalistic',
      });
      if (res.data?.caption) {
        setCaption(res.data.caption);
        toast.success('Caption generated');
      }
    } catch {
      toast.error('Failed to generate caption');
    }
    setGeneratingCaption(false);
  };

  // ── Schedule post (real publish at the scheduled time) ──
  const handleSchedulePost = async () => {
    if (selectedAccountIds.length === 0) { toast.error('Select at least one account'); return; }
    if (!scheduleDate || !scheduleTime) {
      toast.error('Please select both date and time');
      return;
    }
    setPublishing(true);
    try {
      const savedId = await handleSave();
      if (!savedId) { toast.error('Save failed'); setPublishing(false); return; }
      const scheduledFor = new Date(`${scheduleDate}T${scheduleTime}`).toISOString();
      await apiClient.post('/carousel/schedule', {
        composition_id: savedId,
        account_ids: selectedAccountIds,
        caption,
        scheduled_at: scheduledFor,
      });
      toast.success(`Carousel scheduled for ${new Date(scheduledFor).toLocaleString()}`);
      navigate('/campaigns');
    } catch (e) {
      if (handleApiError && handleApiError(e)) { setPublishing(false); return; }
      toast.error(e.response?.data?.detail || 'Failed to schedule');
    }
    setPublishing(false);
  };

  // ── Publish now (real IG carousel + FB multi-photo, async + polling) ──
  const pollCarouselJob = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    let elapsed = 0;
    pollRef.current = setInterval(async () => {
      elapsed += 3;
      try {
        const res = await apiClient.get(`/social/publish-status/${jobId}`);
        const job = res.data;
        if (job.phase === 'rendering') {
          setPublishProgress('Rendering slides…');
        } else {
          const done = (job.accounts || []).filter(a => a.status === 'success' || a.status === 'failed').length;
          setPublishProgress(`Publishing… ${done}/${job.total} done${elapsed > 15 ? ' — carousels can take a moment' : ''}`);
        }
        if (job.status === 'complete') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setPublishProgress('');
          setPublishing(false);
          const okCount = job.success_count ?? (job.accounts || []).filter(a => a.ok).length;
          const total = job.total || selectedAccountIds.length;
          if (okCount >= total) {
            toast.success(`Carousel published to ${okCount} account${okCount > 1 ? 's' : ''}!`);
            navigate('/campaigns');
          } else if (okCount > 0) {
            const failed = (job.accounts || []).filter(a => !a.ok);
            toast.warning(`Published to ${okCount} of ${total}. ${failed.length} failed: ${failed.map(f => f.error).filter(Boolean).join(' | ')}`);
          } else {
            const msg = (job.accounts || []).map(a => a.error).filter(Boolean)[0] || 'Publishing failed';
            toast.error(msg);
          }
        }
      } catch {
        // transient — keep polling
      }
      if (elapsed > 480 && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setPublishProgress('');
        setPublishing(false);
        toast.error('Timed out waiting for publish. It may still complete — check your social accounts.');
      }
    }, 3000);
  };

  const handlePublishNow = async () => {
    if (selectedAccountIds.length === 0) { toast.error('Select at least one account'); return; }
    setPublishing(true);
    setPublishProgress('Saving…');
    try {
      const savedId = await handleSave();
      if (!savedId) { toast.error('Save failed'); setPublishing(false); return; }
      const res = await apiClient.post('/carousel/publish-async', {
        composition_id: savedId,
        account_ids: selectedAccountIds,
        caption,
      });
      setPublishProgress('Rendering slides…');
      pollCarouselJob(res.data.job_id);
    } catch (err) {
      if (handleApiError && handleApiError(err)) { setPublishing(false); return; }
      setPublishing(false);
      setPublishProgress('');
      toast.error(err.response?.data?.detail || 'Failed to publish');
    }
  };

  // ── Save as draft ──
  const handleSaveDraft = async () => {
    setPublishing(true);
    try {
      const savedId = await handleSave();
      if (!savedId) { toast.error('Save failed'); setPublishing(false); return; }
      await apiClient.post('/campaigns', {
        type: 'carousel',
        content_data: { composition_id: savedId },
        caption,
        name: name || 'Carousel Draft',
      });
      toast.success('Saved as draft');
      navigate('/campaigns');
    } catch (e) {
      if (handleApiError && handleApiError(e)) { setPublishing(false); return; }
      toast.error('Failed to save draft');
    }
    setPublishing(false);
  };

  const BG_PRESETS = ['#1a1a2e', '#0f172a', '#1e1b4b', '#0c0a09', '#ffffff', '#f8fafc', '#fef3c7', '#ecfdf5'];

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="carousel-editor">
      {/* Top bar */}
      <div className="flex items-center gap-3 px-4 py-2 border-b border-border/50 bg-card">
        <Button variant="ghost" size="sm" onClick={() => navigate('/carousel-editor')} data-testid="carousel-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> My Carousels
        </Button>
        <div className="w-px h-6 bg-border/30" />
        <span className="text-sm font-semibold">Carousel Maker</span>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="max-w-[240px] h-8 text-sm border-transparent hover:border-border focus:border-border"
          data-testid="carousel-name-input"
        />
        <span className="text-xs text-muted-foreground">{slides.length} slides</span>
        <div className="flex-1" />

        {/* Aspect Ratio selector */}
        <div className="flex items-center gap-1 bg-muted/30 rounded-lg p-0.5">
          {Object.keys(ASPECT_CONFIGS).map(r => (
            <button key={r}
              className={`px-2.5 py-1 text-xs rounded-md transition-colors ${aspectRatio === r ? 'bg-white shadow text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'}`}
              onClick={() => handleAspectChange(r)}
              data-testid={`aspect-${r.replace(':', '-')}`}
            >{r}</button>
          ))}
        </div>

        <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={handleSave} disabled={saving} data-testid="carousel-save-btn">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save
        </Button>
        <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5"
          onClick={() => setShowSchedulePanel(!showSchedulePanel)} data-testid="carousel-schedule-toggle">
          <MessageSquare className="h-3.5 w-3.5" />
          Caption & Schedule
          <ChevronRight className={`h-3 w-3 transition-transform ${showSchedulePanel ? 'rotate-180' : ''}`} />
        </Button>
        <Button size="sm" className="h-8 text-xs gap-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
          onClick={handleRender} disabled={rendering} data-testid="carousel-render-btn">
          {rendering ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
          Export All
        </Button>
      </div>

      {/* Canva-style text toolbar */}
      {selectedLayer?.type === 'text' && (
        <CanvaTextToolbar layer={selectedLayer} onUpdate={updateLayerProps} />
      )}

      {/* Caption & Schedule Panel */}
      {showSchedulePanel && (
        <div className="border-b border-border/50 bg-card/80 px-6 py-4 animate-in slide-in-from-top-2 duration-200" data-testid="carousel-schedule-panel">
          <div className="max-w-4xl mx-auto space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Caption & Schedule</h3>
              <button onClick={() => setShowSchedulePanel(false)} className="p-1 rounded hover:bg-muted">
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>

            {/* Property data summary */}
            {propertyData && (propertyData.address || propertyData.price) && (
              <div className="flex items-center gap-3 text-xs bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="carousel-property-summary">
                <span className="font-semibold text-emerald-700 whitespace-nowrap">Property loaded</span>
                <span className="text-emerald-600 truncate">
                  {[
                    propertyData.address,
                    propertyData.price && `${propertyData.price}`,
                    propertyData.beds && `${propertyData.beds}bd`,
                    propertyData.baths && `${propertyData.baths}ba`,
                    propertyData.sqft && `${propertyData.sqft}sqft`,
                  ].filter(Boolean).join(' · ')}
                </span>
              </div>
            )}

            {/* Account selector — publish to IG + FB together */}
            <div className="space-y-1.5" data-testid="carousel-account-selector">
              <label className="text-xs font-medium text-muted-foreground">
                Accounts <span className="text-muted-foreground/60">(select one or more)</span>
              </label>
              {loadingAccounts ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Loading accounts...</div>
              ) : accounts.length === 0 ? (
                <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  No connected accounts. Go to Social Accounts to connect Instagram or Facebook.
                </div>
              ) : (
                <div className="border border-border/40 rounded-md divide-y divide-border/30">
                  {accounts.map(a => {
                    const checked = selectedAccountIds.includes(a.account_id);
                    const isIg = a.platform === 'instagram';
                    return (
                      <button
                        key={a.account_id}
                        type="button"
                        onClick={() => toggleAccount(a.account_id)}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs transition-colors ${checked ? 'bg-purple-500/10' : 'hover:bg-muted/40'}`}
                        data-testid={`carousel-account-option-${a.account_id}`}
                        aria-pressed={checked}
                      >
                        <span className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${checked ? 'bg-purple-500 border-purple-500' : 'border-border/60'}`}>
                          {checked && <CheckCircle className="h-3 w-3 text-white" />}
                        </span>
                        {isIg ? <Instagram className="h-3.5 w-3.5 text-pink-500 shrink-0" /> : <Facebook className="h-3.5 w-3.5 text-blue-500 shrink-0" />}
                        <span className="truncate">{a.account_name}</span>
                        <span className="ml-auto text-[10px] text-muted-foreground uppercase">{a.platform}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              {selectedAccountIds.length > 0 && (
                <div className="text-[11px] text-muted-foreground" data-testid="carousel-selected-count">
                  {selectedAccountIds.length} account{selectedAccountIds.length > 1 ? 's' : ''} selected
                </div>
              )}
            </div>

            {/* Caption */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-medium text-muted-foreground">Caption</label>
                <Button variant="ghost" size="sm" className="h-6 text-xs gap-1 px-2"
                  onClick={generateCaption} disabled={generatingCaption} data-testid="carousel-generate-caption-btn">
                  {generatingCaption ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                  {propertyData ? 'AI Generate from Property' : 'AI Generate'}
                </Button>
              </div>
              <textarea
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm min-h-[80px] resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                placeholder={propertyData?.address ? `Write a caption for ${propertyData.address}...` : 'Write your carousel caption...'}
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                data-testid="carousel-caption-input"
              />
            </div>

            {/* Schedule */}
            <div className="flex items-end gap-3 flex-wrap">
              <div className="flex-1 min-w-[160px]">
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  <Calendar className="h-3 w-3 inline mr-1" />Date
                </label>
                <Input type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)}
                  className="h-9 text-sm" data-testid="carousel-schedule-date" />
              </div>
              <div className="flex-1 min-w-[120px]">
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  <Clock className="h-3 w-3 inline mr-1" />Time
                </label>
                <Input type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)}
                  className="h-9 text-sm" data-testid="carousel-schedule-time" />
              </div>

              <Button variant="outline" size="sm" className="h-9 gap-1.5"
                onClick={handleSaveDraft} disabled={publishing} data-testid="carousel-save-draft-btn">
                <Save className="h-3.5 w-3.5" />
                Save Draft
              </Button>
              <Button variant="outline" size="sm" className="h-9 gap-1.5"
                onClick={handleSchedulePost} disabled={publishing || !scheduleDate || !scheduleTime || selectedAccountIds.length === 0} data-testid="carousel-schedule-btn">
                {publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Clock className="h-3.5 w-3.5" />}
                Schedule
              </Button>
              <Button size="sm" className="h-9 gap-1.5 bg-green-600 hover:bg-green-700"
                onClick={handlePublishNow} disabled={publishing || selectedAccountIds.length === 0} data-testid="carousel-publish-btn">
                {publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                Publish Now
              </Button>
            </div>

            {/* Publish progress */}
            {publishing && publishProgress && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-md text-xs bg-blue-500/10 text-blue-600 border border-blue-500/20" data-testid="carousel-publish-progress">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span>{publishProgress}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main editor layout — 3-column on desktop, canvas-first on mobile.
          Mobile hides the side panels by default and surfaces them via a
          small bottom toolbar (Assets / Layers) that opens them as drawers
          right above the SlideStrip. */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Background controls — desktop only, mobile uses bottom sheet */}
        <div className={`${isMobile ? 'hidden' : 'w-56'} border-r border-border/50 p-3 overflow-y-auto flex flex-col gap-3`}>
          <div>
            <h3 className="text-xs font-semibold mb-2 flex items-center gap-1.5"><Palette className="h-3.5 w-3.5" /> Background</h3>
            <div className="grid grid-cols-4 gap-1.5 mb-2">
              {BG_PRESETS.map(c => (
                <button key={c}
                  className={`w-full aspect-square rounded-lg border-2 transition-all ${activeSlide?.background_color === c && !activeSlide?.background_image ? 'border-purple-500 scale-95' : 'border-border/30 hover:border-purple-400/50'}`}
                  style={{ backgroundColor: c }}
                  onClick={() => handleBgColorChange(c)}
                  data-testid={`bg-color-${c.replace('#', '')}`}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input type="color" value={activeSlide?.background_color || '#1a1a2e'}
                onChange={(e) => handleBgColorChange(e.target.value)}
                className="h-8 w-8 rounded cursor-pointer" data-testid="bg-custom-color" />
              <span className="text-[10px] text-muted-foreground">Custom</span>
            </div>
          </div>

          {/* Upload Images */}
          <div>
            <h3 className="text-xs font-semibold mb-2 flex items-center gap-1.5"><Image className="h-3.5 w-3.5" /> Images</h3>
            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleBgImageUpload} />
            <Button variant="outline" size="sm" className="w-full h-8 text-xs gap-1"
              onClick={() => fileInputRef.current?.click()} data-testid="bg-image-upload-btn">
              <Upload className="h-3 w-3" /> Upload Images
            </Button>
            {activeSlide?.background_image && (
              <Button variant="ghost" size="sm" className="w-full h-6 text-[10px] mt-1 text-destructive"
                onClick={() => updateSlide(activeSlideIdx, { background_image: null })}
                data-testid="bg-image-remove-btn">
                Remove Background
              </Button>
            )}
          </div>

          {/* Uploaded Images Gallery */}
          {uploadedImages.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold mb-2 flex items-center gap-1.5">
                <LayoutGrid className="h-3.5 w-3.5" /> Uploads
                <span className="text-[10px] text-muted-foreground ml-auto">{uploadedImages.length}</span>
              </h3>
              <p className="text-[10px] text-muted-foreground mb-1.5">Click image or drag to canvas</p>
              {selectedImageUrl && <p className="text-[10px] text-purple-400 mb-1.5">Image selected — click a slide to place it</p>}
              <div className="grid grid-cols-3 gap-1.5" data-testid="uploaded-images-grid">
                {uploadedImages.map(img => (
                  <div key={img.id} className="relative group">
                    <div
                      className={`w-full aspect-square rounded-lg overflow-hidden cursor-pointer transition-all border-2 ${selectedImageUrl === img.url ? 'border-purple-500 ring-2 ring-purple-500/30' : 'border-transparent hover:border-purple-400/50'}`}
                      onMouseUp={(e) => { e.stopPropagation(); handleImageClick(img.url); }}
                      draggable
                      onDragStart={(e) => handleSidebarDragStart(e, img.url)}
                      data-testid={`uploaded-img-${img.id}`}
                    >
                      <img src={img.url} alt={img.name}
                        className="w-full h-full object-cover pointer-events-none" draggable={false} />
                    </div>
                    <button
                      className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity z-10 shadow"
                      onClick={(e) => { e.stopPropagation(); removeUploadedImage(img.id); }}
                      data-testid={`remove-img-${img.id}`}
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Property photos quick-add */}
          {propertyData?.photos?.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold mb-2 flex items-center gap-1.5"><LayoutGrid className="h-3.5 w-3.5" /> Property Photos</h3>
              <p className="text-[10px] text-muted-foreground mb-1.5">Click to apply or drag to canvas</p>
              <div className="grid grid-cols-3 gap-1.5">
                {propertyData.photos.slice(0, 9).map((photo, i) => (
                  <div key={i}
                    className={`w-full aspect-square rounded-lg overflow-hidden cursor-pointer transition-all border-2 ${selectedImageUrl === photo ? 'border-purple-500 ring-2 ring-purple-500/30' : 'border-transparent hover:border-purple-400/50'}`}
                    onMouseUp={(e) => { e.stopPropagation(); handleImageClick(photo); }}
                    draggable
                    onDragStart={(e) => handleSidebarDragStart(e, photo)}
                    data-testid={`prop-photo-${i}`}
                  >
                    <img src={photo} alt={`Property ${i + 1}`}
                      className="w-full h-full object-cover pointer-events-none" draggable={false} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Center: Canvas (with drop zone) */}
        <div className={`${isMobile ? 'flex-1' : 'flex-1'} flex flex-col items-center justify-center gap-4 p-4 min-w-0`}
          onDragOver={handleCanvasDragOver}
          onDragLeave={handleCanvasDragLeave}
          onDrop={handleCanvasDrop}>
          <div className={`relative rounded-xl transition-all ${dragOverCanvas ? 'ring-4 ring-purple-500/50 ring-offset-4 ring-offset-background' : ''}`}>
            {dragOverCanvas && (
              <div className="absolute inset-0 z-20 rounded-xl bg-purple-500/10 border-2 border-dashed border-purple-500 flex items-center justify-center pointer-events-none">
                <span className="text-sm font-medium text-purple-400 bg-background/80 px-3 py-1.5 rounded-lg">Drop to set background</span>
              </div>
            )}
            <SlideCanvas
            slide={activeSlide}
            canvasW={config.previewW}
            canvasH={config.previewH}
            designW={config.w}
            designH={config.h}
            selectedLayerId={selectedLayerId}
            onSelectLayer={(id) => {
              setSelectedLayerId(id);
              setIsBgSelected(false);
              if (isMobile && id) setMobileSheet('layers');
            }}
            onUpdateLayer={updateLayerProps}
            isBgSelected={isBgSelected}
            onBgSelect={() => { setIsBgSelected(true); setSelectedLayerId(null); }}
            onBgTransformUpdate={updateBgTransform}
          />
          </div>
        </div>

        {/* Right: Layers + Inspector — desktop only, mobile uses bottom sheet */}
        <div className={`${isMobile ? 'hidden' : 'w-72'} border-l border-border/50 overflow-y-auto flex flex-col gap-3 p-3`}>
          <LayerPanel
            layers={activeSlide?.layers || []}
            selectedId={selectedLayerId}
            onSelect={(id) => { setSelectedLayerId(id); setIsBgSelected(false); }}
            onLayersChange={updateLayers}
            propertyData={propertyData}
          />
          <PropertyInspector layer={selectedLayer} onUpdate={updateLayerProps} />
        </div>
      </div>

      {/* Mobile toolbar — Assets / Layers icon buttons, shown above SlideStrip */}
      {isMobile && (
        <>
          <div className="flex border-t border-border/50 bg-card" data-testid="carousel-mobile-toolbar">
            {[
              { id: 'assets', label: 'Assets', icon: Image },
              { id: 'layers', label: 'Layers', icon: Layers },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setMobileSheet(id)}
                className="flex-1 flex flex-col items-center justify-center gap-1 py-2 active:bg-muted"
                data-testid={`carousel-toolbar-${id}`}
              >
                <Icon className="h-5 w-5" />
                <span className="text-[10px] font-medium">{label}</span>
              </button>
            ))}
          </div>
          <MobileCoachMark
            storageKey="realprop.carousel.mobile-coach.v1"
            label="Tap Assets or Layers to edit your slide"
            bottomOffsetPx={120}
          />
        </>
      )}

      {/* Mobile bottom sheets */}
      {isMobile && mobileSheet && (
        <div className="fixed inset-0 z-50 flex flex-col" data-testid="carousel-mobile-sheet">
          <div className="flex-1 bg-black/40" onClick={() => setMobileSheet(null)} />
          <div className="bg-card rounded-t-2xl shadow-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-3 border-b border-border/50 sticky top-0 bg-card rounded-t-2xl">
              <div className="text-sm font-semibold capitalize">{mobileSheet}</div>
              <button onClick={() => setMobileSheet(null)} className="p-2 -mr-2 rounded-md hover:bg-muted" aria-label="Close" data-testid="carousel-sheet-close">
                <ChevronDown className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-3 space-y-3">
              {mobileSheet === 'layers' && (
                <>
                  <LayerPanel
                    layers={activeSlide?.layers || []}
                    selectedId={selectedLayerId}
                    onSelect={(id) => { setSelectedLayerId(id); setIsBgSelected(false); }}
                    onLayersChange={updateLayers}
                    propertyData={propertyData}
                  />
                  <PropertyInspector layer={selectedLayer} onUpdate={updateLayerProps} />
                </>
              )}
              {mobileSheet === 'assets' && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-xs font-semibold mb-2 flex items-center gap-1.5"><Palette className="h-3.5 w-3.5" /> Background</h3>
                    <div className="grid grid-cols-4 gap-1.5 mb-2">
                      {BG_PRESETS.map(c => (
                        <button key={c}
                          className={`w-full aspect-square rounded-lg border-2 transition-all ${activeSlide?.background_color === c && !activeSlide?.background_image ? 'border-purple-500 scale-95' : 'border-border/30 hover:border-purple-400/50'}`}
                          style={{ backgroundColor: c }}
                          onClick={() => handleBgColorChange(c)}
                        />
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xs font-semibold mb-2 flex items-center gap-1.5"><Image className="h-3.5 w-3.5" /> Upload Image</h3>
                    <Button variant="outline" size="sm" className="w-full h-9 text-xs gap-1"
                      onClick={() => fileInputRef.current?.click()}>
                      <Upload className="h-3 w-3" /> Upload from device
                    </Button>
                  </div>
                  {propertyData?.photos?.length > 0 && (
                    <div>
                      <h3 className="text-xs font-semibold mb-2">Property Photos</h3>
                      <div className="grid grid-cols-3 gap-1.5">
                        {propertyData.photos.slice(0, 12).map((photo, i) => (
                          <div key={i}
                            className={`w-full aspect-square rounded-lg overflow-hidden cursor-pointer border-2 ${selectedImageUrl === photo ? 'border-purple-500 ring-2 ring-purple-500/30' : 'border-transparent'}`}
                            onClick={() => { handleImageClick(photo); setMobileSheet(null); }}
                          >
                            <img src={photo} alt={`Property ${i+1}`} className="w-full h-full object-cover" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bottom: Slide Strip */}
      <SlideStrip
        slides={slides}
        activeIndex={activeSlideIdx}
        onSelect={handleSlideSelect}
        onAdd={addSlide}
        onDuplicate={duplicateSlide}
        onRemove={removeSlide}
        onReorder={reorderSlides}
      />
    </div>
  );
};
